import {useQuery, useMutation} from '@tanstack/react-query';

import {api} from '../utils/api';
import {ApiError, ApiResponse} from '../types/api';
import type {WeatherGrid, WeatherSpread} from '../types/weatherGrid';

export interface FFDIResult {
    ffdi: number;
    category: string;
}

export interface FFDIParams {
    temperature: number;
    humidity: number;
    windSpeed: number;
    droughtFactor: number;
}

export const calculateFFDI = async (params: FFDIParams): Promise<FFDIResult> => {
    const response = await api.request<ApiResponse<FFDIResult>>(
        `/api/weather/calculate-ffdi?temperature=${params.temperature}&humidity=${params.humidity}&wind_speed=${params.windSpeed}&drought_factor=${params.droughtFactor}`
    );
    return response.data;
};

export const useCalculateFFDI = () => {
    return useMutation({
        mutationFn: calculateFFDI,
    });
};

export interface ApiWeather {
    weather: any;
    /**
     * Spatial lattice returned by the forecast endpoint (null for small
     * projects / model weather / while loading). Only USE_WEATHER_FORECAST
     * populates these; the custom-weather branch always yields null.
     */
    weatherGrid: WeatherGrid | null;
    /** Per-hour [min, max] across the lattice, keyed like interval_weather. */
    spread: WeatherSpread | null;
}

/** Shape of GET /api/weather/forecast's `data` envelope. */
export interface ForecastWeatherData {
    drought_factor?: number | null;
    /** Keetch-Byram Drought Index averaged over the window, mm; null when unknown. */
    drought_index?: number | null;
    interval_weather?: Record<string, any>;
    weather_grid?: WeatherGrid | null;
    spread?: WeatherSpread | null;
    [key: string]: any;
}

export const useWeather = (
    type: string,
    project_id: string,
    start_time: string,
    num_hours: number,
    min_temp: number,
    max_temp: number,
    wind_speed: number,
    min_humidity: number,
    max_humidity: number,
    wind_direction: number,
    isVisible: boolean): ApiWeather => {
    if (type === "USE_WEATHER_FORECAST") {
        const {
            data: weather
        } = useQuery({
            queryKey: ['weather_forecast', start_time, num_hours, project_id, type],
            queryFn: () => api.request<ApiResponse<ForecastWeatherData>>('/api/weather/forecast?start_time=' + encodeURIComponent(start_time) + '&duration=' + num_hours + '&project_id=' + project_id),
            enabled: !!(isVisible && start_time && num_hours && project_id),
            refetchOnWindowFocus: false
        }) as { data: ApiResponse<ForecastWeatherData>; isLoading: boolean; error: ApiError };

        return {
            weather,
            weatherGrid: weather?.data?.weather_grid ?? null,
            spread: weather?.data?.spread ?? null,
        };
    } else if (type === "DEFINE_CUSTOM_WEATHER") {
        const roundedMinTemp = Math.round(min_temp);
        const roundedMaxTemp = Math.round(max_temp);
        const isEnabled =
            isVisible &&
            !!start_time &&
            !!project_id &&
            Number.isFinite(num_hours) &&
            Number.isFinite(min_temp) &&
            Number.isFinite(max_temp) &&
            Number.isFinite(wind_speed) &&
            Number.isFinite(min_humidity) &&
            Number.isFinite(max_humidity) &&
            Number.isFinite(wind_direction);

        const {
            data: weather
        } = useQuery({
            queryKey: ['weather_model', start_time, num_hours, roundedMinTemp, roundedMaxTemp, wind_speed, min_humidity, max_humidity, wind_direction, type],
            queryFn: () => api.request<ApiResponse<any>>('/api/weather/model?start_time=' + encodeURIComponent(start_time) + '&duration=' + num_hours + '&min_temp=' + roundedMinTemp + '&max_temp=' + roundedMaxTemp + '&wind_speed=' + wind_speed + '&min_humidity=' + min_humidity + '&max_humidity=' + max_humidity + '&wind_direction=' + wind_direction),
            enabled: isEnabled,
            refetchOnWindowFocus: false
        }) as { data: ApiResponse<{}>; isLoading: boolean; error: ApiError };

        return {weather, weatherGrid: null, spread: null};
    } else {
        return {weather: null, weatherGrid: null, spread: null}
    }
};
